/**
 * CHS Cart Discount Handler
 *
 * Purpose:
 * - Allows multiple Shopify discount codes to be applied from the cart/cart drawer.
 * - Automatically applies the free shipping discount code ASSOCIATION-PICKUP
 *   when a qualifying Association product discount code is active.
 *
 * Important Shopify limitation:
 * - Shopify Admin discount tags are NOT exposed to Liquid or storefront JavaScript.
 * - Because of that, the "Association" tag must be mirrored manually below
 *   in ASSOCIATION_PRODUCT_CODES.
 */

(() => {
  /**
   * Product discount codes that represent Shopify Admin discount tag: Association.
   *
   * Add every product discount code here that should automatically activate
   * the free shipping code ASSOCIATION-PICKUP.
   *
   * Example:
   * const ASSOCIATION_PRODUCT_CODES = [
   *   'TDP15',
   *   'ASSOCIATION20',
   *   'TEAMDISCOUNT',
   * ];
   */
  const ASSOCIATION_PRODUCT_CODES = [
    'TDP15',
  ];

  /**
   * Free shipping discount code that should be automatically stacked
   * when an Association product discount is active.
   */
  const ASSOCIATION_SHIPPING_CODE = 'ASSOCIATION-PICKUP';

  /**
   * Normalize discount codes to avoid case/space mismatches.
   */
  const normalizeCode = (code) => String(code || '').trim().toUpperCase();

  /**
   * Compare two discount codes safely.
   */
  const sameCode = (a, b) => normalizeCode(a) === normalizeCode(b);

  /**
   * Remove empty and duplicate discount codes.
   */
  const uniqueCodes = (codes) => {
    const seen = new Set();
    const cleanCodes = [];

    (codes || []).forEach((code) => {
      const normalized = normalizeCode(code);

      if (!normalized || seen.has(normalized)) return;

      seen.add(normalized);
      cleanCodes.push(normalized);
    });

    return cleanCodes;
  };

  /**
   * Check if a code list contains a specific code.
   */
  const hasCode = (codes, code) => (
    uniqueCodes(codes).some((item) => sameCode(item, code))
  );

  /**
   * Check if one code is part of the manually mapped Association codes.
   */
  const isAssociationProductCode = (code) => (
    ASSOCIATION_PRODUCT_CODES.some((associationCode) => sameCode(associationCode, code))
  );

  /**
   * Check if the cart has at least one Association product discount code.
   */
  const hasAssociationProductCode = (codes) => (
    uniqueCodes(codes).some(isAssociationProductCode)
  );

  /**
   * Main rule:
   * - If cart has an Association product code, force-add ASSOCIATION-PICKUP.
   * - If cart does not have an Association product code, remove ASSOCIATION-PICKUP.
   */
  const hydrateAssociationCodes = (codes) => {
    let cleanCodes = uniqueCodes(codes);

    if (hasAssociationProductCode(cleanCodes)) {
      if (!hasCode(cleanCodes, ASSOCIATION_SHIPPING_CODE)) {
        cleanCodes.push(normalizeCode(ASSOCIATION_SHIPPING_CODE));
      }

      return cleanCodes;
    }

    return cleanCodes.filter((code) => !sameCode(code, ASSOCIATION_SHIPPING_CODE));
  };

  /**
   * Compare two code arrays regardless of order.
   */
  const codesMatch = (left, right) => {
    const leftCodes = uniqueCodes(left);
    const rightCodes = uniqueCodes(right);

    return (
      leftCodes.length === rightCodes.length
      && leftCodes.every((code) => rightCodes.some((rightCode) => sameCode(code, rightCode)))
    );
  };

  /**
   * Safe cart update URL.
   */
  const getCartUpdateUrl = () => {
    if (window.routes?.cart_update_url) {
      return window.routes.cart_update_url.endsWith('.js')
        ? window.routes.cart_update_url
        : `${window.routes.cart_update_url}.js`;
    }

    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/cart/update.js`;
  };

  /**
   * Safe cart JSON URL.
   */
  const getCartJsonUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/cart.js`;
  };

  /**
   * Extract discount codes from Shopify cart JSON.
   */
  const extractCodesFromDiscountCodeObjects = (discountCodes) => (
    Array.isArray(discountCodes)
      ? discountCodes.map((discount) => discount?.code).filter(Boolean)
      : []
  );

  /**
   * Get rendered discount codes from the DOM.
   *
   * This reads discount pills or labels using:
   * data-discount-code="CODE"
   */
  const getCodesFromDom = () => (
    Array.from(document.querySelectorAll('[data-discount-code]'))
      .map((element) => element.dataset.discountCode)
      .filter(Boolean)
  );

  /**
   * Get the most accurate active discount list.
   *
   * Priority:
   * - Shopify /cart.js response.
   * - Existing rendered DOM discount pills.
   */
  const getCodesFromCart = async () => {
    try {
      const response = await fetch(getCartJsonUrl(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return getCodesFromDom();

      const cart = await response.json();
      const cartCodes = extractCodesFromDiscountCodeObjects(cart.discount_codes);

      return uniqueCodes([
        ...getCodesFromDom(),
        ...cartCodes,
      ]);
    } catch (error) {
      console.warn('[CartDiscount] Unable to read cart discount codes:', error);
      return getCodesFromDom();
    }
  };

  /**
   * Apply all discount codes to the Shopify cart.
   *
   * Shopify supports multiple codes through the discount parameter:
   * discount: "CODE1,CODE2"
   */
  const postCartDiscounts = async (
    codes,
    sections = [],
    sectionsUrl = window.location.pathname,
  ) => {
    const body = {
      discount: uniqueCodes(codes).join(','),
    };

    if (sections.length) {
      body.sections = sections;
      body.sections_url = sectionsUrl;
    }

    const response = await fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart discounts');
    }

    return response.json();
  };

  /**
   * Checkout guard.
   *
   * Purpose:
   * - If the customer reaches checkout while an Association product discount
   *   is already active but the shipping code is missing, this applies the
   *   shipping code before checkout submission.
   *
   * This is useful when cart contents update dynamically and the UI has not
   * refreshed yet.
   */
  const installCheckoutGuard = () => {
    if (window.__chsAssociationCheckoutGuardInstalled) return;

    window.__chsAssociationCheckoutGuardInstalled = true;

    document.addEventListener('submit', async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('[name="checkout"]')) return;
      if (form.dataset.associationDiscountReady === 'true') return;

      const currentCodes = await getCodesFromCart();
      const hydratedCodes = hydrateAssociationCodes(currentCodes);

      if (codesMatch(currentCodes, hydratedCodes)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const checkoutButton = form.querySelector('[name="checkout"]');

      if (checkoutButton) checkoutButton.disabled = true;

      try {
        await postCartDiscounts(hydratedCodes);

        form.dataset.associationDiscountReady = 'true';

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(checkoutButton || undefined);
        } else {
          form.submit();
        }
      } catch (error) {
        console.error(
          '[CartDiscount] Failed to sync Association shipping discount before checkout:',
          error,
        );

        form.dataset.associationDiscountReady = 'true';
        form.submit();
      } finally {
        if (checkoutButton) checkoutButton.disabled = false;
      }
    }, true);
  };

  installCheckoutGuard();

  if (!customElements.get('cart-discount')) {
    class CartDiscount extends HTMLElement {
      constructor() {
        super();

        /**
         * Main discount form elements.
         */
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.button = this.form?.querySelector('button[type="submit"]');

        /**
         * Error elements.
         *
         * Expected markup:
         * .cart-discount__error
         * .cart-discount__error--discount
         * .cart-discount__error--shipping
         */
        this.errorWrap = this.querySelector('.cart-discount__error');
        this.errorDiscount = this.querySelector('.cart-discount__error--discount');
        this.errorShipping = this.querySelector('.cart-discount__error--shipping');

        /**
         * Bind event handlers once so they can be safely removed later.
         */
        this.onApply = this.applyDiscount.bind(this);
        this.onRemove = this.removeDiscount.bind(this);
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('submit', this.onApply);
        }

        this.addEventListener('click', this.onRemove);
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.onApply);
        }

        this.removeEventListener('click', this.onRemove);
      }

      /**
       * Current rendered discount codes.
       *
       * Kept for compatibility with your existing theme logic.
       */
      getCurrentCodes() {
        return uniqueCodes(
          Array.from(document.querySelectorAll('[data-discount-code]'))
            .map((element) => element.dataset.discountCode)
            .filter(Boolean),
        );
      }

      /**
       * Define sections that must be refreshed after discount update.
       *
       * This supports:
       * - Main cart page.
       * - Cart footer/totals.
       * - Cart icon bubble.
       * - Live region.
       * - Cart drawer.
       */
      getSectionsToRender() {
        const main = document.getElementById('main-cart-items');
        const mainSectionId = main?.dataset.id;

        const mainFooter = document.getElementById('main-cart-footer');
        const mainFooterSectionId = mainFooter?.dataset.id;

        const sections = [];

        if (mainSectionId) {
          sections.push({
            id: 'main-cart-items',
            section: mainSectionId,
            selector: '.js-contents',
          });
        }

        if (mainFooterSectionId) {
          sections.push(
            {
              id: 'main-footer-cart-discount',
              section: mainFooterSectionId,
              selector: '.js-contents-discount',
            },
            {
              id: 'main-cart-footer',
              section: mainFooterSectionId,
              selector: '.js-contents',
            },
          );
        }

        sections.push(
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'cart-live-region-text',
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'cart-drawer',
            section: 'cart-drawer',
            selector: null,
          },
        );

        return sections;
      }

      /**
       * Apply discount from form input.
       *
       * If the entered code is an Association code, this automatically adds
       * ASSOCIATION-PICKUP before sending the update to Shopify.
       */
      async applyDiscount(event) {
        event.preventDefault();

        const code = normalizeCode(this.input?.value);

        if (!code) return;

        this.toggleError(false);
        this.setLoading(true);

        const existingCodes = await getCodesFromCart();
        const nextCodes = hydrateAssociationCodes([
          ...existingCodes,
          code,
        ]);

        await this.updateDiscounts(nextCodes, code);

        if (this.input) {
          this.input.value = '';
        }
      }

      /**
       * Remove discount from a discount pill/button.
       *
       * Expected markup:
       * data-remove-discount="CODE"
       *
       * If the removed code is the last Association product discount,
       * ASSOCIATION-PICKUP is also removed automatically.
       */
      async removeDiscount(event) {
        const pill = event.target.closest('[data-remove-discount]');

        if (!pill) return;

        event.preventDefault();

        this.toggleError(false);
        this.setLoading(true);

        const codeToRemove = normalizeCode(pill.dataset.removeDiscount);
        const currentCodes = await getCodesFromCart();

        const keepCodes = currentCodes.filter((code) => !sameCode(code, codeToRemove));
        const nextCodes = hydrateAssociationCodes(keepCodes);

        await this.updateDiscounts(nextCodes, codeToRemove);
      }

      /**
       * Main discount update method.
       *
       * Responsibilities:
       * - Applies normalized codes.
       * - Adds/removes Association shipping code automatically.
       * - Refreshes cart sections.
       * - Re-renders cart drawer if present.
       * - Restores upsell checkbox state after render.
       * - Shows correct discount/shipping error messages.
       */
      async updateDiscounts(codes, lastTouchedCode = '') {
        try {
          const checkboxUpsell = document.querySelector('#cart-upsell-checkbox');

          const sectionsMeta = this.getSectionsToRender();
          const sections = sectionsMeta.map((sectionMeta) => sectionMeta.section);

          const finalCodes = hydrateAssociationCodes(codes);

          const data = await postCartDiscounts(
            finalCodes,
            sections,
            window.location.pathname,
          );

          /**
           * Shopify may return a discount code but mark it as not applicable.
           * If a product discount is invalid, show regular discount error.
           */
          const failedDiscount = this.getFailedDiscount(data, finalCodes);

          if (failedDiscount && !sameCode(failedDiscount.code, ASSOCIATION_SHIPPING_CODE)) {
            this.toggleError(true, 'discount_code');
            return;
          }

          /**
           * Render cart drawer first if the theme uses it.
           */
          const cartDrawer = document.querySelector('cart-drawer');

          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          }

          /**
           * Refresh all non-drawer sections.
           */
          sectionsMeta.forEach(({ id, section, selector }) => {
            if (section === 'cart-drawer') return;

            const container = document.getElementById(id);
            const htmlString = data.sections?.[section];

            if (!container || !htmlString) return;

            const doc = new DOMParser().parseFromString(htmlString, 'text/html');
            const fragment = selector ? doc.querySelector(selector) : doc.body;

            if (!fragment) return;

            const target = selector
              ? container.querySelector(selector) || container
              : container;

            target.innerHTML = fragment.innerHTML;
          });

          /**
           * Shipping discounts can be valid but not visibly displayed in some cart UIs
           * because shipping rates are finalized at checkout.
           *
           * This message is optional but preserved from your original logic.
           */
          if (this.shouldShowShippingCheckoutMessage(data, finalCodes, lastTouchedCode)) {
            this.toggleError(true, 'shipping');
          }

          /**
           * Restore dynamic upsell checkbox after cart HTML refresh.
           */
          setTimeout(() => {
            const newCheckbox = document.querySelector('#cart-upsell-checkbox');

            if (newCheckbox && checkboxUpsell) {
              const variantId = newCheckbox.getAttribute('data-variant-id');

              if (Array.isArray(data.items) && variantId) {
                const hasUpsellInCart = data.items.some((item) => (
                  String(item.variant_id) === String(variantId)
                ));

                newCheckbox.checked = hasUpsellInCart;
              }
            }

            /**
             * Notify other theme scripts that the cart changed.
             */
            document.dispatchEvent(new CustomEvent('cartUpdated'));
          }, 100);
        } catch (error) {
          console.error('[CartDiscount] updateDiscounts error:', error);
          this.toggleError(true, 'discount_code');
        } finally {
          this.setLoading(false);
        }
      }

      /**
       * Find a discount code Shopify accepted syntactically but marked as not applicable.
       */
      getFailedDiscount(data, expectedCodes) {
        if (!Array.isArray(data?.discount_codes)) return null;

        return data.discount_codes.find((discountCode) => (
          discountCode?.applicable === false
          && expectedCodes.some((code) => sameCode(code, discountCode.code))
        ));
      }

      /**
       * Detect if the shipping code was accepted but is not rendered as a visible pill.
       *
       * Some themes do not show shipping codes in cart because shipping is calculated
       * later in checkout. This does not always mean the code failed.
       */
      shouldShowShippingCheckoutMessage(data, expectedCodes, lastTouchedCode) {
        if (!hasCode(expectedCodes, ASSOCIATION_SHIPPING_CODE)) return false;

        const touchedAssociationCode = isAssociationProductCode(lastTouchedCode);
        const touchedShippingCode = sameCode(lastTouchedCode, ASSOCIATION_SHIPPING_CODE);

        if (!touchedAssociationCode && !touchedShippingCode) return false;

        const returnedCodes = extractCodesFromDiscountCodeObjects(data?.discount_codes);
        const renderedCodes = this.getCodesFromRenderedSections(data);

        return (
          hasCode(returnedCodes, ASSOCIATION_SHIPPING_CODE)
          && !hasCode(renderedCodes, ASSOCIATION_SHIPPING_CODE)
        );
      }

      /**
       * Read discount codes from returned section HTML.
       */
      getCodesFromRenderedSections(data) {
        const codes = [];

        Object.values(data?.sections || {}).forEach((htmlString) => {
          if (!htmlString) return;

          const doc = new DOMParser().parseFromString(htmlString, 'text/html');

          doc.querySelectorAll('[data-discount-code]').forEach((element) => {
            codes.push(element.dataset.discountCode);
          });
        });

        return uniqueCodes(codes);
      }

      /**
       * Show/hide discount errors.
       */
      toggleError(show, type = 'discount_code') {
        if (!this.errorWrap) return;

        if (!show) {
          this.errorWrap.classList.add('hidden');
          this.errorDiscount?.classList.add('hidden');
          this.errorShipping?.classList.add('hidden');
          return;
        }

        this.errorWrap.classList.remove('hidden');

        /**
         * Always hide both first so old errors do not remain visible.
         */
        this.errorDiscount?.classList.add('hidden');
        this.errorShipping?.classList.add('hidden');

        if (type === 'discount_code') {
          this.errorDiscount?.classList.remove('hidden');
        }

        if (type === 'shipping') {
          this.errorShipping?.classList.remove('hidden');
        }
      }

      /**
       * Toggle submit button loading/disabled state.
       */
      setLoading(isLoading) {
        if (!this.button) return;

        this.button.disabled = isLoading;
        this.button.classList.toggle('loading', isLoading);
      }
    }

    customElements.define('cart-discount', CartDiscount);
  }
})();